/**
 * FormField Component
 *
 * A reusable form field component that handles different field types
 * with consistent styling and validation display.
 */

import React, { useState, useEffect } from 'react';
import { Form, Alert, Spinner } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { getEnhancedFieldComponent } from './EnhancedFieldTypes';
import masterDataService from '../../../services/masterDataService';

const FormField = ({ field, fieldIndex = 0, value, onChange, error, disabled = false, formId }) => {
  // State for dynamically loaded options
  const [options, setOptions] = useState(field.options || []);
  const [loading, setLoading] = useState(false);

  // Fetch options if field uses master data
  useEffect(() => {
    const loadOptions = async () => {
      if (field.master_data_table && field.master_data_column && formId) {
        setLoading(true);
        try {
          const response = await masterDataService.getFormFieldOptions(formId, field.field_id);

          if (response.data && response.data.options) {
            setOptions(response.data.options);
          }
        } catch (error) {
          console.error('Error loading field options:', error);
          toast.error(`Failed to load options for ${field.label}`);
        } finally {
          setLoading(false);
        }
      } else if (field.options) {
        // Use static options if no master data configuration
        setOptions(field.options);
      }
    };

    loadOptions();
  }, [field.field_id, field.master_data_table, field.master_data_column, formId, field.options]);

  const handleChange = (e) => {
    const { type, checked, files } = e.target;
    let newValue = e.target.value;

    if (type === 'checkbox') {
      newValue = checked;
    } else if (type === 'file') {
      newValue = files[0];
    } else if (type === 'number') {
      newValue = newValue === '' ? '' : parseFloat(newValue);
    }

    onChange(newValue);
  };

  const renderField = () => {
    // Generate composite key for this field instance
    const getFieldKey = (field, index) => {
      const baseId = field.field_id || field.id;
      return `${baseId}_${index}`;
    };

    const baseProps = {
      id: field.field_id,
      name: getFieldKey(field, fieldIndex),
      'data-field-id': field.field_id,
      value: value,
      onChange: handleChange,
      disabled: disabled,
      placeholder: field.placeholder || `Enter ${field.label}`,
      required: field.required,
      className: `form-control ${error ? 'is-invalid' : ''}`
    };

    switch (field.field_type) {
      case 'text':
        return (
          <Form.Control
            type="text"
            {...baseProps}
            minLength={field.min_length}
            maxLength={field.max_length}
          />
        );

      case 'textarea':
        return (
          <Form.Control
            as="textarea"
            rows={4}
            {...baseProps}
            minLength={field.min_length}
            maxLength={field.max_length}
          />
        );

      case 'number':
        return (
          <Form.Control
            type="number"
            {...baseProps}
            min={field.min_value}
            max={field.max_value}
            step="any"
            inputMode="decimal"
            onWheel={(e) => e.currentTarget.blur()}
            onKeyDown={(e) => {
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault();
              }
            }}
          />
        );

      case 'email':
        return (
          <Form.Control
            type="email"
            {...baseProps}
          />
        );

      case 'date':
        return (
          <Form.Control
            type="date"
            {...baseProps}
          />
        );

      case 'dropdown':
      case 'select':
        return (
          <>
            <Form.Select
              {...baseProps}
              value={value || ''}
              disabled={disabled || loading}
            >
              <option value="">
                {loading ? 'Loading options...' : `Select ${field.label}`}
              </option>
              {options && options.map((option, index) => {
                // Handle both string options and object options {value, label}
                const optionValue = typeof option === 'string' ? option : (option.value || option);
                const optionLabel = typeof option === 'string' ? option : (option.label || option.value || option);

                return (
                  <option key={index} value={optionValue}>
                    {optionLabel}
                  </option>
                );
              })}
            </Form.Select>
            {loading && (
              <div className="text-muted small mt-1">
                <Spinner animation="border" size="sm" className="me-1" />
                Loading options from master data...
              </div>
            )}
            {field.master_data_table && !loading && (
              <Form.Text className="text-muted">
                <small>Source: Master Data ({options.length} options)</small>
              </Form.Text>
            )}
          </>
        );

      case 'radio':
        return (
          <div>
            {field.options && field.options.map((option, index) => (
              <Form.Check
                key={index}
                type="radio"
                id={`${field.field_id}_${index}`}
                name={field.field_id}
                value={option}
                label={option}
                checked={value === option}
                onChange={handleChange}
                disabled={disabled}
                className={error ? 'is-invalid' : ''}
              />
            ))}
          </div>
        );

      case 'checkbox':
        if (field.options && field.options.length > 1) {
          // Multiple checkboxes
          const selectedValues = Array.isArray(value) ? value : [];
          
          return (
            <div>
              {field.options.map((option, index) => (
                <Form.Check
                  key={index}
                  type="checkbox"
                  id={`${field.field_id}_${index}`}
                  value={option}
                  label={option}
                  checked={selectedValues.includes(option)}
                  onChange={(e) => {
                    const newValues = [...selectedValues];
                    if (e.target.checked) {
                      newValues.push(option);
                    } else {
                      const idx = newValues.indexOf(option);
                      if (idx > -1) newValues.splice(idx, 1);
                    }
                    onChange(newValues);
                  }}
                  disabled={disabled}
                  className={error ? 'is-invalid' : ''}
                />
              ))}
            </div>
          );
        } else {
          // Single checkbox
          return (
            <Form.Check
              type="checkbox"
              id={field.field_id}
              label={field.placeholder || field.label}
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
              disabled={disabled}
              className={error ? 'is-invalid' : ''}
            />
          );
        }

      case 'checkboxes':
        // Multiple checkboxes field type
        const selectedValues = Array.isArray(value) ? value : [];
        
        return (
          <div>
            {field.options && field.options.map((option, index) => {
              // Handle both string and object options
              const optionValue = typeof option === 'string' ? option : (option.value || option.label || option);
              const optionLabel = typeof option === 'string' ? option : (option.label || option.value || option);
              
              return (
                <Form.Check
                  key={index}
                  type="checkbox"
                  id={`${field.field_id}_${index}`}
                  value={optionValue}
                  label={optionLabel}
                  checked={selectedValues.includes(optionValue)}
                  onChange={(e) => {
                    const newValues = [...selectedValues];
                    if (e.target.checked) {
                      newValues.push(optionValue);
                    } else {
                      const idx = newValues.indexOf(optionValue);
                      if (idx > -1) newValues.splice(idx, 1);
                    }
                    onChange(newValues);
                  }}
                  disabled={disabled}
                  className={error ? 'is-invalid' : ''}
                />
              );
            })}
          </div>
        );

      case 'file_upload':
        return (
          <div>
            <Form.Control
              type="file"
              {...baseProps}
              value={undefined} // File inputs should not have a value prop
            />
            {value && (
              <small className="text-muted mt-1 d-block">
                Selected: {value.name || 'File selected'}
              </small>
            )}
          </div>
        );

      // Enhanced field types
      case 'time':
      case 'datetime':
      case 'signature':
      case 'header':
      case 'markdown':
      case 'paragraph':
      case 'phone':
      case 'url':
      case 'barcode':
      case 'diagram':
      case 'image_upload':
      case 'document_upload':
      case 'formula':
        const EnhancedComponent = getEnhancedFieldComponent(field.field_type);
        if (EnhancedComponent) {
          return (
            <EnhancedComponent
              field={field}
              value={value}
              onChange={(fieldId, newValue) => onChange(newValue)}
              error={error}
              disabled={disabled}
            />
          );
        }
        // Fallback to default if component not found
        return (
          <Alert variant="warning">
            Unsupported field type: {field.field_type}
          </Alert>
        );

      default:
        return (
          <Form.Control
            type="text"
            {...baseProps}
          />
        );
    }
  };

  // Special handling for enhanced field types that have their own Form.Group and Form.Label
  // These field types should not be wrapped in an additional Form.Group to avoid duplicate labels
  const enhancedFieldTypes = ['header', 'markdown', 'paragraph', 'time', 'datetime', 'signature', 'phone', 'url', 'barcode', 'diagram', 'image_upload', 'document_upload', 'formula'];
  if (enhancedFieldTypes.includes(field.field_type)) {
    return renderField();
  }

  return (
    <Form.Group className="mb-3">
      <Form.Label htmlFor={field.field_id}>
        {field.label}
        {field.required && <span className="text-danger ms-1">*</span>}
      </Form.Label>
      
      {field.help_text && (
        <Form.Text className="text-muted d-block mb-2">
          {field.help_text}
        </Form.Text>
      )}
      
      {renderField()}
      
      {error && (
        <div className="invalid-feedback d-block">
          {error}
        </div>
      )}
      
      {/* Character count for text fields */}
      {(field.field_type === 'text' || field.field_type === 'textarea') && 
       field.max_length && 
       value && (
        <Form.Text className="text-muted">
          {value.length}/{field.max_length} characters
        </Form.Text>
      )}
    </Form.Group>
  );
};

export default FormField;